import React, { useEffect, useMemo, useState } from 'react';
import { useUserToEditAtom } from '../../atoms/users/useUserToEditAtom';
import { getAllUsers } from '../../api/authenticateAPIs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../../css/pages/all_users_list.css';

import useAuth from "../../hooks/useAuth";

// Bez diakritiky a bez rozdielu veľkosti písmen: „grac" nájde „Grác".
const normalize = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const UserList = () => {

  const { auth } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [, setUserToEdit] = useUserToEditAtom();
  const [t, ] = useTranslation('global');
  const [userList, setUserList] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.uri !== 1) {
      navigate('/Unauthorized');
      return;
    }
  }, [auth.uri, navigate]);

  // Používatelia sa načítajú raz (a pri prepnutí „Zobraziť deaktivovaných“); hľadanie potom
  // filtruje tento zoznam okamžite na FE. Aktívni sú navrchu, deaktivovaní pod nimi.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getAllUsers(showInactive)
      .then((users) => {
        if (cancelled) return;
        const list = [...(users || [])].sort((a, b) => (a.isActive === false) - (b.isActive === false));
        setUserList(list);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        if (!cancelled) setUserList([]);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [showInactive]);

  // Všetky zadané slová sa musia nájsť v mene, priezvisku alebo e-maile.
  const filteredUsers = useMemo(() => {
    const words = normalize(searchString).split(/\s+/).filter(Boolean);
    if (words.length === 0) return userList;
    return userList.filter((u) => {
      const haystack = normalize(`${u.userFirstname} ${u.userLastname} ${u.userEmail}`);
      return words.every((w) => haystack.includes(w));
    });
  }, [userList, searchString]);

  const handleUserClick = (id) => {
    const userDetails = userList.find((user) => user.id === id);
    setUserToEdit(userDetails);
    navigate('/EditUser');
  };

  const handleAddUser = () => {
    navigate("/CreateUser");
  }

  const handleGoBack = () => {
    navigate('/');
  };

  return (
    <div className="all-users-list page-wrap">
      <button className="return-button return-button-top" onClick={handleGoBack}>
        <span className="material-symbols-outlined">arrow_back</span>{t('return')}
      </button>

      <div className="header-title">
        <h1>{t('userList')}</h1>

        <div className='user-buttons-container'>
          <button className="add-button" onClick={handleAddUser}>
              <span className="material-symbols-outlined">add</span>
          </button>
        </div>
      </div>

      <div className="user-searchbar">
        <input
          type="text"
          name="name"
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          placeholder={t('searchByUsername')}
          autoFocus
        />
        {searchString ? (
          <button type="button" className="user-search-clear" onClick={() => setSearchString('')} aria-label={t('cancel')}>
            <span className="material-symbols-outlined">close</span>
          </button>
        ) : (
          <span className="material-symbols-outlined user-search-icon" aria-hidden="true">search</span>
        )}
      </div>

      <label className="user-inactive-toggle">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        <span>{t('showInactive')}</span>
      </label>

      <div className={`user-detail-wrapper ${isLoading ? 'center-loader' : ''}`}>
      {isLoading ? (
        <div className="lds-ellipsis">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        ) : filteredUsers.length === 0 ? (
          <p className="user-detail no-users-message">{t('noResults')}</p>
        ) : (
          filteredUsers.map((user) => (
            <div key={user.id} className={`user-detail${user.isActive === false ? ' inactive' : ''}`} onClick={() => handleUserClick(user.id)}>
              <div className="user-icon">
                {((user.userFirstname || '?').charAt(0) + (user.userLastname || '').charAt(0)).toUpperCase()}
              </div>
              <span className="user-name-label">{[user.userFirstname, user.userLastname].filter(Boolean).join(' ') || user.userEmail}</span>
              <span className="user-email-label">{user.userEmail}</span>
              <span className="user-badges">
                {user.isActive === false ? (
                  <span className="user-status-badge inactive">{t('statusInactive')}</span>
                ) : null}
                {((user.authMethod ?? user.AuthMethod ?? '').toLowerCase() === 'ad') ? (
                  <span className="user-ad-badge">AD</span>
                ) : null}
              </span>
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default UserList;
